import useHttpProvider from "@/common/hooks/use-http";
import { stripePromise } from "@/common/lib/stripe";
import { Button } from "@/components/ui/button";
import { notifyError } from "@/components/ui/sonner";
import { saveBillingAddress } from "@/features/profile/services/profiles";
import {
	AddressElement,
	Elements,
	PaymentElement,
	useElements,
	useStripe,
} from "@stripe/react-stripe-js";
import type { Appearance } from "@stripe/stripe-js";
import { useState } from "react";

const appearance: Appearance = {
	theme: "stripe",
	variables: {
		colorPrimary: "#004096",
		colorBackground: "#ffffff",
		colorText: "#0f172a",
		borderRadius: "16px",
		fontFamily: "system-ui, sans-serif",
	},
};

export type AddressMode = "billing" | "shipping" | "none";

export type DefaultAddress = {
	name?: string;
	line1?: string;
	line2?: string;
	city?: string;
	state?: string;
	postalCode?: string;
	country?: string;
	phone?: string;
};

type Props = {
	clientSecret: string;
	mode: "subscription" | "payment";
	addressMode?: AddressMode;
	defaultAddress?: DefaultAddress;
	confirmLabel?: string;
	cancelLabel?: string;
	onComplete: () => void;
	onCancel: () => void;
};

export default function InlineStripeCheckout({
	clientSecret,
	mode,
	addressMode = "none",
	defaultAddress,
	confirmLabel,
	cancelLabel = "Cancel",
	onComplete,
	onCancel,
}: Props) {
	return (
		<Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
			<CheckoutForm
				mode={mode}
				addressMode={addressMode}
				defaultAddress={defaultAddress}
				confirmLabel={confirmLabel}
				cancelLabel={cancelLabel}
				onComplete={onComplete}
				onCancel={onCancel}
			/>
		</Elements>
	);
}

function CheckoutForm({
	mode,
	addressMode,
	defaultAddress,
	confirmLabel,
	cancelLabel,
	onComplete,
	onCancel,
}: {
	mode: "subscription" | "payment";
	addressMode: AddressMode;
	defaultAddress?: DefaultAddress;
	confirmLabel?: string;
	cancelLabel?: string;
	onComplete: () => void;
	onCancel: () => void;
}) {
	const stripe = useStripe();
	const elements = useElements();
	const { http } = useHttpProvider();
	const [isPaying, setIsPaying] = useState(false);

	async function persistAddress() {
		if (addressMode === "none" || !elements) return;
		const addressEl = elements.getElement("address");
		if (!addressEl) return;
		const { value } = await addressEl.getValue();
		const a = value?.address;
		if (!a?.line1 || !a.city || !a.state || !a.postal_code) return;
		try {
			await saveBillingAddress(http, {
				line1: a.line1,
				line2: a.line2 ?? undefined,
				city: a.city,
				state: a.state,
				postalCode: a.postal_code,
				country: a.country,
				phone: value.phone ?? undefined,
			});
		} catch (err) {
			// non-blocking — payment is what matters; log so we can debug
			console.error("[Checkout] Failed to persist address:", err);
		}
	}

	async function onPay() {
		if (!stripe || !elements) return;

		setIsPaying(true);

		// Persist the address up-front so it survives a 3DS redirect.
		await persistAddress();

		const { error, paymentIntent } = await stripe.confirmPayment({
			elements,
			confirmParams: {
				return_url: `${window.location.origin}${window.location.pathname}`,
			},
			redirect: "if_required",
		});

		if (error) {
			setIsPaying(false);
			notifyError(
				"Payment failed",
				error.message ?? "Could not process payment. Please try again."
			);
			return;
		}

		if (
			paymentIntent?.status === "succeeded" ||
			paymentIntent?.status === "processing"
		) {
			setIsPaying(false);
			onComplete();
			return;
		}

		setIsPaying(false);
	}

	const defaultConfirmLabel =
		mode === "subscription" ? "Pay & subscribe" : "Pay";

	const addressDefaultValues = defaultAddress
		? {
				name: defaultAddress.name,
				phone: defaultAddress.phone,
				address: {
					line1: defaultAddress.line1 ?? "",
					line2: defaultAddress.line2 ?? "",
					city: defaultAddress.city ?? "",
					state: defaultAddress.state ?? "",
					postal_code: defaultAddress.postalCode ?? "",
					country: defaultAddress.country ?? "AU",
				},
			}
		: { address: { country: "AU" } };

	return (
		<div className="flex flex-col gap-4 rounded-2xl bg-white p-4 md:p-6">
			{addressMode !== "none" && (
				<AddressElement
					options={{
						mode: addressMode,
						fields: { phone: "always" },
						defaultValues: addressDefaultValues,
					}}
				/>
			)}
			<PaymentElement options={{ layout: "tabs" }} />
			<div className="flex flex-col items-center gap-6 pt-2">
				<Button
					type="button"
					variant="default"
					className="w-full"
					disabled={!stripe || isPaying}
					onClick={onPay}
				>
					{isPaying ? "Processing…" : (confirmLabel ?? defaultConfirmLabel)}
				</Button>
				<button
					type="button"
					onClick={onCancel}
					disabled={isPaying}
					className="web-body-b3 text-gray-500 underline-offset-4 hover:text-gray-700 hover:underline disabled:opacity-50"
				>
					{cancelLabel}
				</button>
			</div>
		</div>
	);
}
